import * as React from "react";
import { WebPortalClient, FvcSession } from "webportalclient";
import { FvcAnalysisSet } from "webportalclient";
import { LocalizedDateTime, DateTimeAccuracy } from "../misc/components/LocalizedDateTime";
import { Link } from "react-router-dom";
import { ErrorBoundary } from "../errors/ErrorBoundary";
import { useReferenceContextProvider } from "../Core/ReferenceContextProvider";
import * as Moment from "moment";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { Authorization } from "../misc/components/Authorization";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import { useSecurityContext } from "../Core/SecurityContext";
import { useWebPortalApiClient } from "../misc/api/WebPortalTokenClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

const HoursLeft = (dateReceived) => {
    return Moment.duration(Moment.utc().diff(Moment(dateReceived))).asHours();
}

const StatusIcon = ({ hoursLeft }) => {
    let tooltip = "";

    if (hoursLeft === 47) {
        tooltip = "This session must be urgently over-read. There is 1 hour left until the deadline."
    } else if (hoursLeft > 47) {
        tooltip = "This session must be urgently over-read. It is past the deadline!";
    } else if (hoursLeft >= 45) {
        tooltip = `This session must be urgently over-read. There are ${48 - hoursLeft} hours left until the deadline.`;
    } else if (hoursLeft >= 24) {
        tooltip = `This session requires over-reading. There are ${48 - hoursLeft} hours left until the deadline.`;
    }


    if (hoursLeft >= 45) {
        return (<FontAwesomeIcon icon={faTriangleExclamation} className="fa-fw text-danger" aria-hidden="true" data-toggle="tooltip" title={tooltip} />);
    } else {
        return (<i className="fas fa-fw">&nbsp;</i>);
    }
}

interface SpirometrySessionWidgetItemProps {
    analysisSet: FvcAnalysisSet;
}

const SpirometrySessionWidgetItem = ({ analysisSet }: SpirometrySessionWidgetItemProps) => {
    const { overreadStatuses } = useReferenceContextProvider();

    const hoursLeft = HoursLeft(analysisSet.session.lastReceived)

    return (
        <tr className={hoursLeft >= 45 ? "danger" : (hoursLeft >= 24 ? "warning" : "")}>
            <td><StatusIcon hoursLeft={hoursLeft} /></td>
            <td>{analysisSet.session.visit.subject.site.study.protocol}</td>
            <td>{overreadStatuses.find(or => or.id === analysisSet.status).name}</td>
            <td><LocalizedDateTime time={analysisSet.session.lastReceived} accuracy={DateTimeAccuracy.Second} isTooltip={true} /></td>
            <td className="text-right">
                <div className="btn-group" role="group">
                    <button type="button" className="btn btn-primary btn-xs dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        <FontAwesomeIcon aria-hidden="true" icon={faBars} />
                    </button>
                    <ul className="dropdown-menu scrollable-menu" role="menu">
                        <li>
                            <Link to={`/spirometry/analysis/${analysisSet.id}/view`}>View</Link>
                        </li>
                        <li>
                            <Link to={`/spirometry/analysis/${analysisSet.id}/overread`}>Over-Read</Link>
                        </li>
                    </ul>
                </div>
            </td>
        </tr>
    );
}

export const SpirometrySessionWidgetTable = () => {
    const webPortalClient = useWebPortalApiClient();
    const [analysisSets, setAnalysisSets] = React.useState<FvcAnalysisSet[]>();

    React.useEffect(() => {
        const getData = async () => {
            const analysisSetResult = await webPortalClient.client.getSpirometryAnalysisSets("id,session,status", `type eq 'SAS_OVERREAD' and (status eq 'ORS_PENDING' or status eq 'ORS_NOT_STARTED')`, `session/lastReceived`, "session($select=lastReceived,visit;$expand=visit($select=subject;$expand=subject($select=site;$expand=site($select=study;$expand=study($select=protocol)))))", 5, undefined, undefined, true);
            setAnalysisSets(analysisSetResult.value);
        }

        getData();

        return () => {
            webPortalClient.abortController.abort();
        }
    }, []);

    if (!analysisSets)
        return <LoadingWithMessage message="Loading Spirometry Sessions..." />

    return (
        <ErrorBoundary>
            <table className="table table-striped table-bordered table-hover">
                <thead>
                    <tr>
                        <th></th>
                        <th>Protocol</th>
                        <th>Status</th>
                        <th>Date Received</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {analysisSets.length === 0 ? <tr>
                        <td colSpan={5}>
                            <p className="text-center">No data available</p>
                        </td>
                    </tr> : null}
                    {analysisSets.map(a => <SpirometrySessionWidgetItem key={a.id} analysisSet={a} />)}
                </tbody>
            </table>
        </ErrorBoundary>
    );
}

export const SpirometrySessionsWidget = () => {
    const { currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();

    return (
        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.ViewFvcSessionsWidget}>
            <ErrorBoundary>
                <div className="col-md-6">
                    <div id="SpirometrySessionsWidget" className="ibox">
                        <div className="ibox-title">
                            <h5 className="text-primary">Spirometry Sessions</h5>
                        </div>
                        <div className="ibox-content">
                            <SpirometrySessionWidgetTable />
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Authorization>
    );
}
